<template>
  <div>
    <v-card class="mb-6">
      <v-card-title>Fréquences configurées</v-card-title>
      <v-row>
        <v-col>
          <v-data-table
            class="ml-6 mr-6"
            :page="pagination.options.page"
            :pageCount="pagination.numberOfPages"
            :headers="headers"
            :items="pagination.items"
            :options.sync="pagination.options"
            :server-items-length="pagination.total"
            :loading="page.loading"
            :disable-filtering="true"
            :disable-sort="true"
            :footer-props="{
              'items-per-page-options': [10, 25, 50],
            }"
            :items-per-page="pagination.options.itemsPerPage"
          >
            <template v-slot:item.site_id="{item}">
              {{ displaySite(item.site_id) }}
            </template>
            <template v-slot:item.edit="{ item }">
              <div v-if="item.site_id">
                <v-btn
                  class="mx-2"
                  elevation="0"
                  fab
                  small
                  @click="editFrequency(item)"
                >
                  <v-icon color="primary">
                    {{ icons.mdiPencil }}
                  </v-icon>
                </v-btn>
                <v-btn
                  class="mx-2"
                  elevation="0"
                  fab
                  small
                  @click="askRemoveFrequency(item)"
                >
                  <v-icon color="secondary">
                    {{ icons.mdiTrashCan }}
                  </v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card>


    <v-dialog v-model="dialogModel.edit">
      <FrequencyCreation
        @close-dialog="cancelUpdateFrequency"
        @saved="refresh"
        :id="page.frequencyToUpdate.id"
        :process-type="processType"
      />
    </v-dialog>

    <confirm-modal 
      :open="dialogModel.confirm"
      :question="`Voulez-vous réellement supprimer la fréquence '${page.frequencyToUpdate.name}' ?`" 
      @cancel="dialogModel.confirm = false" 
      @confirm="removeFrequency" />

    <v-snackbar
      v-model="page.showSuccessMessage"
      color="success"
      timeout="3000"
      top
    >
      Modification enregistrée
    </v-snackbar>
  </div>
</template>

<script>
import { computed, onMounted, reactive, watch } from "@vue/composition-api";
import { mdiPencil, mdiTrashCan } from "@mdi/js";

import ConfirmModal from "@/components/common/modals/Confirm";
import FrequencyCreation from "@/components/ai-factory/full-auto/cards/FrequencyCreation.vue";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";
import { zIndex } from "@/utils/constants";
import { addTimeLeadingZero } from "@/utils/formatting";

export default {
  name: "ConfigTable",
  components: {
    ConfirmModal,
    FrequencyCreation,
  },
  props: {
    processType: {
      type: String,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { axiosGet, axiosDelete } = useAxios();
    const { store } = useProxy();

    const page = reactive({
      forceCall: false,
      loading: false,
      isOptionsInitialized: false,
      showSuccessMessage: false,
      frequencyToUpdate: {
        id: 0,
        name: "",
        site_id: 0,
        times: [],
      },
    });

    const pagination = reactive({
      numberOfPages: 0,
      total: 0,
      items: [],
      options: {
        page: 1,
        itemsPerPage: 50,
      },
    });

    const dialogModel = reactive({
      edit: false,
      confirm: false,
    });

    // =>  METHODS
    const formatResult = (items) =>
      items.map((item) => ({
        ...item,
        times: item.values.map(time => `${time.hour}:${addTimeLeadingZero(time.minutes)}`).join(", "),
      }));

    const getFrequencies = async () => {
      if ((applyFilters.value || page.forceCall) && !page.loading) {
        try {
          page.loading = true;

          const {
            data: { total, items },
          } = await axiosGet("/aif-auto-configs/frequencies", {
            ...(site.value ? { site_id: site.value } : {}),
            ...(props.processType ? { process_type: props.processType } : {}),
            page: pagination.options.page,
            limit: pagination.options.itemsPerPage,
          });

          pagination.total = total;
          pagination.items = formatResult(items);
        } catch (error) {
          console.error("error while fetching data", error);
        }

        page.loading = false;

        // Force clicking on button again to re-run that request :
        // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
        store.dispatch("common/updateApplyFilters", false);
      }

      page.forceCall = false;
    };

    const cancelUpdateFrequency = async () => {
      dialogModel.edit = false;
    };

    const editFrequency = async (item) => {
      page.frequencyToUpdate = { ...item };
      dialogModel.edit = true;
    };

    const askRemoveFrequency = async (item) => {
      page.frequencyToUpdate = { ...item };
      dialogModel.confirm = true;
    };

    const removeFrequency = async () => {
      dialogModel.confirm = false;
      await axiosDelete(`aif-auto-configs/frequency/${page.frequencyToUpdate.id}`);

      page.forceCall = true;
      await getFrequencies();
    };

    const refresh = async () => {
      page.showSuccessMessage = true;
      page.forceCall = true;
      await getFrequencies();
    };

    const displaySite = (site_id) => siteList.value.find(s => s.id === site_id)?.name ?? "(Tous)";

    // =>  COMPUTED
    const applyFilters = computed(
      () => store.getters["common/getApplyFilters"]
    );
    const site = computed(() => store.getters["aiFactory/getSelectedSites"]);
    const siteList = computed(() => (store.getters["common/getSiteList"] ?? []));

    // =>  WATCH
    watch(applyFilters, async () => {
      pagination.options.page = 1;
      await getFrequencies();
    });

    watch(
      () => pagination.options,
      () => {
        if (page.isOptionsInitialized && !applyFilters.value) {
          // Not on initialization : wait for a real user change
          page.forceCall = true;
        }

        page.isOptionsInitialized = true;
        getFrequencies();
      }
    );

    // =>  HOOKS
    onMounted(async () => {
      await store.dispatch("common/getSiteList");

      page.forceCall = true;
      await getFrequencies();
    });

    // =>  DATA
    return {
      askRemoveFrequency,
      cancelUpdateFrequency,
      dialogModel,
      displaySite,
      editFrequency,
      headers: [
        { text: "Nom", value: "name" },
        { text: "Site", value: "site_id" },
        { text: "Horaires", value: "times" },
        { text: "Actions", value: "edit" },
      ],
      icons: {
        mdiPencil,
        mdiTrashCan,
      },
      page,
      pagination,
      refresh,
      removeFrequency,
      zIndex,
    };
  },
};
</script>

<style scoped>
</style>
